import { type Dispatch, type SetStateAction, useState } from "react";
import { Activity, ClipboardList, Fuel, MapPinned, Plane, ShieldAlert, TerminalSquare } from "lucide-react";
import type { AdminAuditLogRecord, PublicUser } from "../../api";
import type { Aircraft, AirportConfigSet, AirportConfigUpdate, FlightOccurrenceEvent, FlightPlan, GlobalEvent, SaveGame, ScheduleSource, ServiceDay, WorldData } from "../../../domain/types";
import type { GmDraft } from "../../gmEvents";
import { EventList } from "../../components/EventList";
import { PanelHeader } from "../../components/Panel";
import { GmAircraftPanel } from "./GmAircraftPanel";
import { AirportConfigPanel } from "./GmAirportConfigPanel";
import { AuditLogPanel } from "./GmAuditLogPanel";
import { GmEventForm, EventCatalog } from "./GmEventsPanel";
import { FlightPlanPanel } from "./GmFlightPlanPanel";
import { WorldReference } from "./GmWorldReference";
import { GM_VIEWS, type GmView } from "./gmTypes";


export function GmConsole(props: {
  user: PublicUser | null;
  save: SaveGame | null;
  world: WorldData;
  airportConfigs: AirportConfigSet;
  schedule: ScheduleSource;
  activeEvents: GlobalEvent[];
  adminEvents: GlobalEvent[];
  auditLog: AdminAuditLogRecord[];
  gmDraft: GmDraft;
  setGmDraft: Dispatch<SetStateAction<GmDraft>>;
  onCreateCustomEvent: () => void;
  onActivateEvent: (eventId: string) => void;
  onCancelEvent: (eventId: string) => void;
  onCreateFlightPlan: (input: { flightNo: string; origin: string; destination: string; serviceDays: ServiceDay[]; startsDay: number; endsDay?: number; departureTimeLocal: string; minimumTurnMinutes: number; priority: number }) => void;
  onCancelFlightPlan: (flightPlanId: string, reason: string) => void;
  onCancelFlightLeg: (input: { occurrenceId: string; day: number; dateIso: string; reason: string }) => void;
  onCreateFlightOccurrenceEvent: (input: Omit<FlightOccurrenceEvent, "id" | "status" | "createdBy" | "createdAt">) => void;
  onSaveAircraft: (input: Omit<Aircraft, "id"> & { id?: string }) => void;
  onDeleteAircraft: (aircraftId: string) => void;
  onSaveAirportConfig: (input: AirportConfigUpdate) => void;
  onRefresh: () => void;
}) {
  const [view, setView] = useState<GmView>(() => normalizeGmView(new URLSearchParams(window.location.search).get("view")));
  const selectView = (nextView: GmView) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", nextView);
    window.history.replaceState(null, "", url);
    setView(nextView);
  };

  return (
    <section className="gm-workspace">
      <nav className="gm-subnav" aria-label="GM 二级界面">
        {GM_VIEWS.map((item) => (
          <button key={item.id} className={gmViewButtonClassName(view, item.id)} onClick={() => selectView(item.id)}>
            {gmViewIcon(item.id)}
            <span>{item.label}</span>
            <em>{gmViewCount(item.id, props)}</em>
          </button>
        ))}
      </nav>

      {view === "events" && (
        <section className="gm-view gm-events-layout">
          <section className="panel gm-command-panel">
            <PanelHeader icon={<ShieldAlert size={15} />} title="GM 事件下发台" code="CAST" />
            <GmEventForm world={props.world} draft={props.gmDraft} setDraft={props.setGmDraft} onSubmit={props.onCreateCustomEvent} />
          </section>

          <section className="panel gm-active-panel">
            <PanelHeader icon={<Activity size={15} />} title="活动事件缓存" code="LIVE" />
            <EventList events={props.activeEvents} world={props.world} emptyText="当前没有活动事件。玩家侧会在轮询或结算时刷新。" />
          </section>

          <section className="panel gm-events-panel">
            <PanelHeader icon={<ClipboardList size={15} />} title="事件库/反向操作" code="EVDB" />
            <EventCatalog world={props.world} events={props.adminEvents} onActivate={props.onActivateEvent} onCancel={props.onCancelEvent} onRefresh={props.onRefresh} />
          </section>
        </section>
      )}

      {view === "flights" && (
        <section className="gm-view gm-flights-layout">
          <section className="panel gm-flight-panel">
            <PanelHeader icon={<Plane size={15} />} title="航班计划下发/取消" code="FLTP" />
            <FlightPlanPanel
              save={props.save}
              world={props.world}
              schedule={props.schedule}
	              onCreateFlightPlan={props.onCreateFlightPlan}
	              onCancelFlightPlan={props.onCancelFlightPlan}
	              onCancelFlightLeg={props.onCancelFlightLeg}
	              onCreateFlightOccurrenceEvent={props.onCreateFlightOccurrenceEvent}
            />
          </section>
        </section>
      )}

      {view === "aircraft" && (
        <section className="gm-view gm-aircraft-layout">
          <section className="panel gm-aircraft-panel">
            <PanelHeader icon={<Plane size={15} />} title="飞机资料配置" code="ACFT" />
            <GmAircraftPanel save={props.save} world={props.world} onSaveAircraft={props.onSaveAircraft} onDeleteAircraft={props.onDeleteAircraft} />
          </section>
        </section>
      )}

      {view === "airports" && (
        <section className="gm-view gm-airport-layout">
          <section className="panel gm-airport-panel">
            <PanelHeader icon={<Fuel size={15} />} title="机场运行数据配置" code="APT" />
            <AirportConfigPanel world={props.world} airportConfigs={props.airportConfigs} activeEvents={props.activeEvents} onSaveAirportConfig={props.onSaveAirportConfig} />
          </section>
        </section>
      )}

      {view === "world" && (
        <section className="gm-view gm-world-layout">
          <section className="panel gm-world-panel">
            <PanelHeader icon={<MapPinned size={15} />} title="世界数据索引" code="WRLD" />
            <WorldReference world={props.world} />
          </section>
        </section>
      )}

      {view === "audit" && (
        <section className="gm-view gm-audit-layout">
          <section className="panel gm-audit-panel">
            <PanelHeader icon={<TerminalSquare size={15} />} title={`审计日志 / ${props.user?.email ?? "GM"}`} code="AUD" />
            <AuditLogPanel auditLog={props.auditLog} />
          </section>
        </section>
      )}
    </section>
  );
}

function gmViewButtonClassName(activeView: GmView, itemView: GmView): string {
  if (activeView === itemView) {
    return "active";
  }
  return "";
}

function gmViewIcon(view: GmView) {
  if (view === "events") return <ShieldAlert size={14} />;
  if (view === "flights") return <Plane size={14} />;
  if (view === "aircraft") return <Plane size={14} />;
  if (view === "airports") return <Fuel size={14} />;
  if (view === "world") return <MapPinned size={14} />;
  return <TerminalSquare size={14} />;
}

function gmViewCount(
  view: GmView,
  props: {
    activeEvents: GlobalEvent[];
    adminEvents: GlobalEvent[];
    auditLog: AdminAuditLogRecord[];
    schedule: ScheduleSource;
    world: WorldData;
    airportConfigs: AirportConfigSet;
    save?: SaveGame | null;
  }
) {
  if (view === "events") return props.activeEvents.length || props.adminEvents.length;
  if (view === "flights") return props.schedule.flightPlans.length;
  if (view === "aircraft") return props.save?.fleet.length ?? 0;
  if (view === "airports") return props.airportConfigs.ops.length;
  if (view === "world") return props.world.airspaces.length;
  return props.auditLog.length;
}

function normalizeGmView(value: string | null): GmView {
  if (value === "flights" || value === "aircraft" || value === "airports" || value === "world" || value === "audit") {
    return value;
  }
  return "events";
}
